import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { dubaiDate, dubaiDateTime } from '../../../utils/dubaiTime';
import './laundry.css';

function Laundry() {
  const [laundryItems, setLaundryItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusVisibility, setStatusVisibility] = useState({});
  const [lhStainReports, setLhStainReports] = useState([]);
  const [lhStainVisible, setLhStainVisible] = useState(true);

  const navigate = useNavigate();
  const userRole = localStorage.getItem('userRole');
  const storedFirstName = localStorage.getItem('firstName');

  // Function to get status details
  const getStatusDetails = (status) => {
    const statusInfo = {
      created: { label: 'Waiting for warehouse confirmation', color: '#FFA500' },
      processing: { label: 'Processing', color: '#1E90FF' },
      flagged: { label: 'Incorrect Warehouse Confirmation', color: '#DC143C' },
      completed: { label: 'Packed & Ready', color: '#32CD32' },
      delivered: { label: 'Delivered', color: '#32a887' },
      before_photo: { label: 'Awaiting Before Photos', color: '#FFD700' },
      stains_reported: { label: 'Stains Reported', color: '#8A2BE2' }
    };
    return statusInfo[status] || { label: 'Unknown', color: '#808080' };
  };

  // Fetch laundry data from backend
  useEffect(() => {
    const fetchLaundryItems = async () => {
      try {
        const response = await axios.get('/api/laundry');
        const data = Array.isArray(response.data) ? response.data : [];
        setLaundryItems(data);

        const initialVisibility = {};
        data.forEach(item => {
          const statusLabel = getStatusDetails(item.status).label;
          if (!(statusLabel in initialVisibility)) {
            initialVisibility[statusLabel] = true;
          }
        });
        setStatusVisibility(initialVisibility);

        // Fetch LH Stain Reports (past 24 hours)
        try {
          const stainRes = await axios.get('/api/laundry/lh-stain-reports?hours=24');
          const rows = Array.isArray(stainRes.data?.rows) ? stainRes.data.rows : [];
          setLhStainReports(rows);
        } catch (e) {
          console.error('Error fetching LH stain reports:', e);
          setLhStainReports([]);
        }

      } catch (error) {
        console.error('Error fetching laundry items:', error);
        setLaundryItems([]);
      }
    };

    fetchLaundryItems();
  }, []);

  // Toggle visibility of items under each status
  const toggleVisibility = (status) => {
    setStatusVisibility((prev) => ({
      ...prev,
      [status]: !prev[status]
    }));
  };

  // Navigate to specific laundry detail page with restrictions for warehouse staff
  const handleBoxClick = (laundryId, status) => {
    if (userRole !== 'warehouse' || status !== 'created') {
      navigate(`/laundry/${laundryId}`);
    } else {
      alert("Access restricted for this laundry count.");
    }
  };

  // Navigate to confirmation page for created items
  const handleConfirm = (id, e, propertyInfo) => {
    e.stopPropagation();
    navigate(`/laundry/confirm/${id}?propertyId=${propertyInfo.propertyId}&propertyDisplay=${encodeURIComponent(propertyInfo.propertyDisplay)}`);
  };

  // Group items by status label
  const groupedByStatus = laundryItems.reduce((acc, item) => {
    const statusLabel = getStatusDetails(item.status).label;
    if (!acc[statusLabel]) acc[statusLabel] = [];
    acc[statusLabel].push(item);
    return acc;
  }, {});

  // Order of statuses for display
  const sortedStatuses = [
    'Awaiting Before Photos',
    'Stains Reported',
    'Incorrect Warehouse Confirmation',
    'Waiting for warehouse confirmation',
    'Processing',
    'Packed & Ready',
    'Delivered'
  ];

  // Format Property ID to 6 digits
  const formatPropertyId = (id) => (id ?? '').toString().padStart(5, '0');

  // Filter items based on search term
  const filterItems = (item) => {
    const { client_name = '', unit_number = '', building_name = '', property_id } = item;
    const propertyId = formatPropertyId(property_id);

    return (
      client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      unit_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      building_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      propertyId.includes(searchTerm)
    );
  };

  // Filter stain reports based on search term
  const filterStainReport = (r) => {
    const client = (r.client_name || '').toLowerCase();
    const unit = (r.unit_number || '').toLowerCase();
    const building = (r.building_name || '').toLowerCase();
    const pid = formatPropertyId(r.property_id || 0);

    const q = searchTerm.toLowerCase();

    return (
      client.includes(q) ||
      unit.includes(q) ||
      building.includes(q) ||
      pid.includes(searchTerm)
    );
  };

  // Group stain reports by booking_id so multiple reports show in one box
  const groupStainReportsByBooking = (reports) => {
    const map = new Map();

    for (const r of reports) {
      const key = r.booking_id;

      if (!map.has(key)) {
        map.set(key, {
          booking_id: r.booking_id,
          property_id: r.property_id,
          client_name: r.client_name,
          building_name: r.building_name,
          unit_number: r.unit_number,
          earliest_uploaded_at: r.uploaded_at,
          comments: [],
        });
      }

      const group = map.get(key);

      if (r.uploaded_at && new Date(r.uploaded_at) < new Date(group.earliest_uploaded_at)) {
        group.earliest_uploaded_at = r.uploaded_at;
      }

      const c = (r.comment || '').trim();
      if (c) group.comments.push(c);
    }

    const grouped = Array.from(map.values()).map(g => ({
      ...g,
      comments: Array.from(new Set(g.comments)),
    }));

    grouped.sort((a, b) => new Date(b.earliest_uploaded_at) - new Date(a.earliest_uploaded_at));

    return grouped;
  };

  const groupedStainReports = groupStainReportsByBooking(
    lhStainReports.filter(filterStainReport)
  );

  return (
    <div className="laundry-container">
      {userRole === 'warehouse' && (
        <h2>Logged in as: <span style={{ color: '#FF5A60' }}>{storedFirstName}</span></h2>
      )}
      <h2>Laundry Counts</h2>

      <div className="search-and-add-container">
        <div className="search-bar-wrapper">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder="Search by Property ID, Client Name, Unit Number, or Building Name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="search-bar"
          />
        </div>

        <div className='laundry-page-buttons'>
          <Link to="/laundry/report-lh-stain">
            <button className="add-laundry-button">Report LH Stain</button>
          </Link>

          {userRole === 'manager' && (
            <Link to="/laundry/stats">
              <button className="add-laundry-button">Statistics</button>
            </Link>
          )}

          {(userRole === 'manager' || userRole === 'admin' || userRole === 'supervisor') && (
            <Link to="/laundry/new">
              <button className="add-laundry-button">+ Add Laundry Count</button>
            </Link>
          )}
        </div>
      </div>

      {(userRole === 'warehouse' || userRole === 'manager' || userRole === 'admin' || userRole === 'supervisor') && (
        <div className="status-group">
          <button
            className="status-toggle"
            onClick={() => setLhStainVisible(v => !v)}
            style={{ backgroundColor: getStatusDetails('stains_reported').color }}
          >
            LH Stain Reports (Past 48 Hours) ({groupedStainReports.length})
            <span className="toggle-icon">{lhStainVisible ? '▼' : '►'}</span>
          </button>

          {lhStainVisible && (
            <div className="laundry-status-group">
              {groupedStainReports.map((g) => (
                <div
                  key={g.booking_id}
                  className="laundry-box"
                  onClick={() => navigate(`/bookings/${g.booking_id}`)}
                >
                  <div
                    className="status-label"
                    style={{ color: getStatusDetails('stains_reported').color }}
                  >
                    LH Stain Report
                  </div>

                  <p>
                    <strong>Property ID: #{formatPropertyId(g.property_id)}</strong>
                  </p>

                  <p>
                    <strong>
                      {g.client_name || 'Unknown Client'} - {g.building_name || 'N/A'} - Unit {g.unit_number || 'N/A'}
                    </strong>
                  </p>

                  {/* Asia/Dubai render (item 8) */}
                  <p>Reported: {dubaiDateTime(g.earliest_uploaded_at)}</p>

                  {g.comments.length > 0 && (
                    <p style={{ marginTop: 6 }}>
                      <strong>Notes:</strong> {g.comments.join(' | ')}
                    </p>
                  )}
                </div>
              ))}

              {groupedStainReports.length === 0 && (
                <p style={{ padding: 10, opacity: 0.7 }}>
                  No stain reports in the past 48 hours.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {sortedStatuses.map((status) => (
        <div key={status} className="status-group">
          {groupedByStatus[status] && (
            <>
              <button
                className="status-toggle"
                onClick={() => toggleVisibility(status)}
                style={{ backgroundColor: getStatusDetails(status).color }}
              >
                {status} ({groupedByStatus[status].length})
                <span className="toggle-icon">{statusVisibility[status] ? '▼' : '►'}</span>
              </button>

              {statusVisibility[status] && (
                <div className="laundry-status-group">
                  {groupedByStatus[status]
                    .filter(filterItems)
                    .sort((a, b) => {
                      const isDeliveredOrCompleted = ['delivered', 'completed'].includes(a.status);
                      if (isDeliveredOrCompleted) {
                        return new Date(b.created_at) - new Date(a.created_at);
                      }
                      return new Date(a.created_at) - new Date(b.created_at);
                    })
                    .map((item) => (
                      <div
                        key={item.id}
                        className="laundry-box"
                        onClick={() => handleBoxClick(item.id, item.status)}
                      >
                        <div
                          className="status-label"
                          style={{ color: getStatusDetails(item.status).color }}
                        >
                          {getStatusDetails(item.status).label}
                        </div>

                        <p>
                          <strong>Property ID: #{formatPropertyId(item.property_id)}</strong>
                          {item.is_laundry_count_lh && (
                            <strong style={{ color: 'red' }}> - Linen Hire</strong>
                          )}
                        </p>

                        <p>
                          <strong>
                            {item.client_name || 'Unknown Client'} - {item.building_name || 'N/A'} - Unit {item.unit_number || 'N/A'}
                          </strong>
                        </p>

                        {/* Asia/Dubai render (item 8) */}
                        <p>Created: {dubaiDate(item.created_at)}</p>

                        {(item.status === 'created' || item.status === 'flagged') && (userRole === 'manager' || userRole === 'warehouse') && (
                          <button
                            onClick={(e) =>
                              handleConfirm(item.id, e, {
                                propertyId: item.property_id,
                                propertyDisplay: `${item.client_name || 'Unknown Client'} - ${item.building_name || 'N/A'} - Unit ${item.unit_number || 'N/A'}`,
                              })
                            }
                            className="confirm-button"
                          >
                            Confirm Laundry Count
                          </button>
                        )}

                        {item.status === 'before_photo' && (userRole === 'manager' || userRole === 'warehouse') && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/laundry/confirm/${item.id}`, { state: { startStep: 2 } });
                            }}
                            className="confirm-button"
                          >
                            Upload Photos
                          </button>
                        )}

                        {(item.status === 'processing' || item.status === 'stains_reported') &&
                          (userRole === 'manager' || userRole === 'warehouse') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/laundry/packing/${item.id}`);
                              }}
                              className="confirm-button"
                            >
                              Packing
                            </button>
                          )}
                      </div>
                    ))}
                </div>
              )}
            </>
          )}
        </div>
      ))}
    </div>
  );
}

export default Laundry;
